import React, { useState, useRef, useEffect } from 'react';
import { ImageIcon, Globe, Lock, X, Loader } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTheme } from '../../contexts/ThemeContext';
import { btn } from '../../utils/buttonStyles';
import { useAuth } from '../../contexts/AuthContext';
import { useProfileImage } from '../../profile/hooks/useProfileImage';
import { apiCall } from '../../services/apiUtil';
import postImageService from '../services/postImageService';
import { handleUploadError, uploadErrorMessage } from '../../services/imageUploadService';
import { useImagePicker } from '../../hooks/useImagePicker.jsx';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';

/**
 * PostCard — Create-post form with fully internal state.
 *
 * Props:
 * - onSubmit(text, imageUrls, privacy) → Promise<{success, error}> (required)
 * - placeholder           — textarea placeholder text (optional)
 * - maxLength             — character limit (optional, default 4000)
 * - defaultPrivacy        — 'public' | 'private' (optional, default 'public')
 */
const PostCard = ({
  onSubmit,
  onPostUpdated,
  placeholder = "What's on your mind?",
  maxLength = 4000,
  defaultPrivacy = 'public',
  allowVideoUpload = false,
}) => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const { videoUpload: featureVideoUpload } = useFeatureFlags();
  const FEATURE_VIDEO_UPLOAD = allowVideoUpload || featureVideoUpload;
  const { imageUrl: userAvatarUrl } = useProfileImage(user?.id, user?.profileImage);
  const textareaRef = useRef(null);

  const [text, setText] = useState('');
  const [privacy, setPrivacy] = useState(defaultPrivacy);
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingImages, setIsUploadingImages] = useState(false);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [text]);

  const { openPicker, pickerInput } = useImagePicker({
    accept: FEATURE_VIDEO_UPLOAD ? 'image/*,video/*' : 'image/*',
    multiple: true,
    onFiles: handleImageSelect,
  });

  async function handleImageSelect(files) {
    if (files.length + selectedImages.length > 10) {
      toast.error('You can only upload up to 10 images per post');
      return;
    }

    // Convert each image to a JPEG Blob via canvas — same approach as ImageCropModal.
    // This normalises the format (PNG → JPEG), reduces size, and avoids iOS WKWebView
    // fetch() failures that happen with large non-JPEG binary bodies.
    // Videos are passed through unchanged.
    const processFile = (file) => new Promise((resolve) => {
      if (file.type.startsWith('video/')) {
        resolve(file);
        return;
      }
      const img = new window.Image();
      const objUrl = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(objUrl);
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext('2d').drawImage(img, 0, 0);
        canvas.toBlob((blob) => resolve(blob || file), 'image/jpeg', 0.85);
      };
      img.onerror = () => { URL.revokeObjectURL(objUrl); resolve(file); };
      img.src = objUrl;
    });

    const blobs = await Promise.all(files.map(processFile));

    // Preview URLs from the original files (still valid at this point)
    const newPreviewUrls = blobs.map(b => URL.createObjectURL(b));

    setSelectedImages(prev => [...prev, ...blobs]);
    setImagePreviewUrls(prev => [...prev, ...newPreviewUrls]);

  }

  const handleRemoveImage = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
    URL.revokeObjectURL(imagePreviewUrls[index]);
    setImagePreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if ((!text.trim() && selectedImages.length === 0) || isSubmitting) return;

    setIsSubmitting(true);
    try {
      // Step 1: Upload and validate images BEFORE creating the post.
      // If moderation fails we return early — the post is never created.
      let imageKeys = [];
      if (selectedImages.length > 0) {
        setIsUploadingImages(true);
        try {
          const tempId = Date.now().toString(36);
          const uploadResults = await postImageService.uploadPostImages(selectedImages, tempId, user?.id);
          imageKeys = uploadResults.map(r => r.key).filter(Boolean);
        } catch (uploadError) {
          console.error('Failed to upload images:', uploadError);
          if (!handleUploadError(uploadError)) {
            toast.error(uploadErrorMessage(uploadError));
          }
          return;
        } finally {
          setIsUploadingImages(false);
        }
      }

      // Step 2: Create the post (images already validated by Rekognition)
      const result = await onSubmit(text.trim(), [], privacy);
      if (result && result.success === false) {
        toast.error(`Failed to post: ${result.error}`);
        return;
      }

      // Step 3: PATCH the post with the validated image keys
      if (imageKeys.length > 0 && result?.data) {
        const activity = result.data;
        try {
          const patchResult = await apiCall(`/posts/${activity.id}/media`, {
            method: 'PATCH',
            body: JSON.stringify({ imageKeys }),
          });
          if (patchResult?.data && onPostUpdated) {
            onPostUpdated(patchResult.data);
          }
        } catch (patchError) {
          console.error('Failed to attach images to post:', patchError);
        }
      }

      // Reset form
      setText('');
      setSelectedImages([]);
      imagePreviewUrls.forEach(url => URL.revokeObjectURL(url));
      setImagePreviewUrls([]);
      toast.success('Post created successfully!');
    } catch (error) {
      console.error('Error creating post:', error);
      toast.error(`Error creating post: ${error.message}`);
    } finally {
      setIsSubmitting(false);
      setIsUploadingImages(false);
    }
  };

  return (
    <div className={`${theme.card} rounded-lg ${theme.shadow} p-4 mb-6`}>
      <form onSubmit={handleSubmit}>
        <div className="flex items-start gap-x-3 mb-4">
          {userAvatarUrl ? (
            <img
              src={userAvatarUrl}
              alt={user?.firstName || 'User'}
              className="w-10 h-10 rounded-full object-cover flex-shrink-0"
            />
          ) : (
            <div className={`w-10 h-10 ${theme.gradient} rounded-full flex items-center justify-center flex-shrink-0`}>
              <span className="text-lg font-bold text-white">
                {user?.firstName?.charAt(0)?.toUpperCase() || user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </span>
            </div>
          )}
          <div className="flex-1">
            <textarea
              ref={textareaRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={placeholder}
              className={`w-full px-4 py-3 rounded-2xl ${theme.backgroundSecondary} resize-none focus:ring-2 focus:ring-offset-0 border-0 outline-none transition-colors ${theme.text} placeholder-gray-500 overflow-hidden`}
              rows="3"
              maxLength={maxLength}
            />
            <div className="text-xs text-gray-500 mt-1 text-right">
              {text.length} / {maxLength}
            </div>
          </div>
        </div>

        {/* Image Previews */}
        {imagePreviewUrls.length > 0 && (
          <div className="mb-4 px-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {imagePreviewUrls.map((url, index) => {
                const file = selectedImages[index];
                const isVideo = file && file.type.startsWith('video/');
                return (
                  <div key={index} className="relative group">
                    {isVideo ? (
                      <video src={url} className="w-full h-32 object-cover rounded-lg" controls />
                    ) : (
                      <img src={url} alt={`Preview ${index + 1}`} className="w-full h-32 object-cover rounded-lg" />
                    )}
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(index)}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    {isUploadingImages && (
                      <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center">
                        <Loader className="w-6 h-6 text-white animate-spin" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <p className={`text-xs ${theme.textMuted} mt-2`}>{selectedImages.length} / 10 media files selected</p>
          </div>
        )}

        <hr className={`border-t ${theme.border} mb-3`} />

        <div className="flex justify-between items-center gap-2">
          <div className="flex gap-x-2 overflow-x-auto scrollbar-hide whitespace-nowrap -mx-4 px-4 md:mx-0 md:px-0 flex-1">
            {pickerInput}
            <button
              type="button"
              onClick={openPicker}
              className={`flex items-center gap-x-2 px-4 py-2 rounded-lg ${theme.cardHover} transition-colors ${theme.textMuted} hover:${theme.text} cursor-pointer flex-shrink-0`}
            >
              <ImageIcon className="w-5 h-5" />
              <span className="text-sm font-medium">{FEATURE_VIDEO_UPLOAD ? 'Photo/Video' : 'Photo'}</span>
            </button>

            <div className="flex items-center gap-x-2 flex-shrink-0">
              <button
                type="button"
                onClick={() => setPrivacy(privacy === 'public' ? 'private' : 'public')}
                className={`flex items-center gap-x-2 px-3 py-2 rounded-lg ${theme.cardHover} transition-colors ${theme.textMuted} hover:${theme.text}`}
              >
                {privacy === 'public' ? <Globe className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                <span className="text-sm font-medium capitalize">{privacy}</span>
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={(!text.trim() && selectedImages.length === 0) || isSubmitting}
            className={`flex items-center gap-x-2 flex-shrink-0 ${btn.base} ${btn.md} ${theme.buttonPrimary}`}
          >
            {isSubmitting && <Loader className="w-4 h-4 animate-spin" />}
            <span>{isUploadingImages ? 'Uploading...' : isSubmitting ? 'Posting...' : 'Post'}</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default PostCard;
